"use client";

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface TrackingFormProps {
    onTrack: (bookingId: string) => void;
    isLoading: boolean;
}

export const TrackingForm: React.FC<TrackingFormProps> = ({ onTrack, isLoading }) => {
    const [bookingId, setBookingId] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (bookingId.trim()) {
            onTrack(bookingId.trim());
        }
    };

    return (
        <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="w-full max-w-md mx-auto space-y-4 relative z-10"
        >
            <div className="relative group">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="flex gap-2 p-2 bg-card/80 backdrop-blur-xl border border-border/50 rounded-full shadow-lg">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            type="text"
                            placeholder="Enter your Booking ID (e.g., bk-xyz...)"
                            value={bookingId}
                            onChange={(e) => setBookingId(e.target.value)}
                            className="pl-10 h-12 border-none bg-transparent shadow-none focus-visible:ring-0 text-base rounded-full placeholder:text-muted-foreground/50"
                            disabled={isLoading}
                        />
                    </div>
                    <Button
                        type="submit"
                        disabled={!bookingId.trim() || isLoading}
                        className={cn(
                            "rounded-full h-12 px-8 font-medium transition-all duration-300",
                            isLoading ? "w-12 px-0" : "w-auto"
                        )}
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                        ) : (
                            "Track Status"
                        )}
                    </Button>
                </div>
            </div>
            <p className="text-center text-xs text-muted-foreground/60">
                You can find the Booking ID in your confirmation email or WhatsApp message.
            </p>
        </motion.form>
    );
};
